import React, { useCallback, useContext, useRef } from 'react';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import { Button } from 'ncoded-component-library';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { useTranslation } from 'react-i18next';
import FileSecurity from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DataRoomDocumentDrawers/ManageFileAccess/components/FileSecurity';
import { getPayloadBulkFileSettings } from 'router/subrouters/DataRoom/utils';
import ScopeModal from './components/ScopeModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ApplyToAllDataRoomsModal from 'router/subrouters/DataRoom/components/ApplyToAllDataRoomsModal';
import SecurityScopeQuestion from './components/SecurityScopeQuestion';
import useInvalidateFilePreviewCache from 'hooks/useInvalidateFilePreviewCache';
import { scrollToErrorsOnSubmit } from 'utils';

import './DocumentsGlobalSecurity.styles.scss';

type DocumentsGlobalSecurityProps = {
  className?: string;
};

const DocumentsGlobalSecurity: React.FC<DocumentsGlobalSecurityProps> = (
  props,
) => {
  const { className } = props;

  const { dataRoom, doesntHaveAccess, fileSettingsInitialValues, setDataRoom } =
    useContext(DataRoomContext);

  const { defaultsSecuritySettingsSetupAt, defaultsSecuritySettingsSetupBy } =
    dataRoom || {};

  const useDefaults = useRef(false); // true when "Save as default for all DR" is clicked

  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [applyToAllDataRoomsModal, applyToAllDataRoomsModalRef] =
    useCallbackRef<ModalRef>();

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const { invalidateAllFiles } = useInvalidateFilePreviewCache();

  const classes = classNames('documents-global-security', className);

  const { t } = useTranslation();

  const handleScopeQuestion = useCallback(
    (values, change, submit) => {
      const { applyOnExistingFiles } = values || {};
      change('applyOnExistingFiles', applyOnExistingFiles === 'true');
      submit();
      applyToAllDataRoomsModal?.close();
    },
    [applyToAllDataRoomsModal],
  );

  const onSubmit = useCallback(
    async (values) => {
      const { applyOnExistingFiles, ...rest } = values;
      const result = getPayloadBulkFileSettings(rest);

      modal.close();

      const isDefault = useDefaults.current;

      try {
        const { data } = await dataRoomApi.room.updateGlobalSecuritySettings(
          dataRoom._id,
          result,
          applyOnExistingFiles,
          isDefault,
        );
        setDataRoom((prev) => ({ ...prev, ...data }));

        setDataRoom((prev) => ({
          ...prev,
          ...data,
          defaultsSecuritySettingsSetupAt: data.defaultsSecuritySettingsSetupAt,
          defaultsSecuritySettingsSetupBy: data.defaultsSecuritySettingsSetupBy,
        }));

        // existing files' security changed only when applying to existing files;
        // setting future defaults / org defaults doesn't affect cached previews.
        if (applyOnExistingFiles) invalidateAllFiles();

        popSuccess(
          t(
            isDefault
              ? 'saveAsOrganisationDefaultsSuccessMessage'
              : 'saveDataRoomSuccessMessage',
          ),
        );
      } catch (e) {
        popServerError(e);
      }
    },
    [
      dataRoom._id,
      modal,
      popServerError,
      popSuccess,
      setDataRoom,
      invalidateAllFiles,
      t,
    ],
  );

  return (
    <div className={classes}>
      <Form
        initialValues={fileSettingsInitialValues}
        onSubmit={onSubmit}
        keepDirtyOnReinitialize
        render={({
          pristine,
          submitting,
          hasValidationErrors,
          form: { change, submit },
          handleSubmit,
        }) => {
          const submitDisabled = doesntHaveAccess || pristine || submitting;

          return (
            <form onSubmit={handleSubmit}>
              <FileSecurity
                collapsible={false}
                showDefaultsSettingsSetup
                defaultsSettingsSetupAt={defaultsSecuritySettingsSetupAt}
                defaultsSettingsSetupBy={defaultsSecuritySettingsSetupBy}
              >
                <div className="actions">
                  <Button
                    variant="outline"
                    disabled={submitDisabled}
                    onClick={() => {
                      if (hasValidationErrors) {
                        scrollToErrorsOnSubmit(hasValidationErrors, submit);
                        return;
                      }

                      useDefaults.current = true;
                      applyToAllDataRoomsModal?.open();
                    }}
                  >
                    {t('saveAsDefaultsForAllDataRooms')}
                  </Button>
                  <Button
                    disabled={submitDisabled}
                    onClick={() => {
                      if (hasValidationErrors) {
                        scrollToErrorsOnSubmit(hasValidationErrors, submit);
                        return;
                      }

                      useDefaults.current = false;
                      modal?.open();
                    }}
                  >
                    {t('saveToThisDataRoom')}
                  </Button>
                </div>
              </FileSecurity>

              <ApplyToAllDataRoomsModal
                ref={applyToAllDataRoomsModalRef}
                onSubmit={(values) =>
                  handleScopeQuestion(values, change, submit)
                }
              >
                <SecurityScopeQuestion />
              </ApplyToAllDataRoomsModal>

              <ScopeModal
                ref={modalRef}
                onSubmit={(values) =>
                  handleScopeQuestion(values, change, submit)
                }
              >
                <SecurityScopeQuestion />
              </ScopeModal>
            </form>
          );
        }}
      />
    </div>
  );
};

export default DocumentsGlobalSecurity;
